/**
 * 列名動的化レベル 3 (docs/105) の汎用 AI 解析結果レンダラ。
 *
 * `post.aiAnalysis.generic` (= `PostMinimal.aiAnalysisGeneric`) は、出力フォーマット (M3) に
 * `fields[]` が設定されている場合に analyzePost が `validateGenericPayload` で sanitize した
 * 「列名そのまま」の Record<string, unknown> である (mode 非依存)。本コンポーネントはその生データを
 * 型に応じて動的に描画する additive な汎用ビュー。
 *
 * 設計方針 (additive・回帰ゼロ):
 *   - mode 別 result (EcwResultBlock / BousaiResultBlock / WalkerResultBlock) は **置き換えず併存**。
 *     こちらは「動的列の生データ確認」を目的とした別セクション (折りたたみ可、初期は閉)。
 *   - generic が無い / 空オブジェクトのときは呼び出し側で null を渡すため、本体は非空前提で描画する。
 *   - label マスタは存在しないため key 名をそのまま見出しに使う (軽い humanize: snake/camel → 空白区切り)。
 *
 * 値の型ごとの描画 (validateGenericPayload の出力契約に対応):
 *   - 文字列 / 数値 / boolean → テキスト行 (key: value)
 *   - {sum,max,avg} / {sum,avg} 等の数値オブジェクト → 集約スコアの inline 表示
 *   - string[] → chip 群
 *   - Record<string,unknown>[] (classifications 等) → 共通キーを列にしたコンパクトな表
 *   - その他 (ネスト object 等) → JSON 文字列に fallback
 *
 * スタイルは既存 mode 別ブロックの慣習に合わせる (slate 基調 section / text-[11px] ラベル等)。
 * inline style 新規直書きはしない。
 *
 * 列名動的化レベル 3 (docs/105 / old2 移植): `schema` (= `AiOutputFormat.schema`) を渡すと、
 * record-array field (classifications 等) は raw な GenericTable ではなく、`fields[]` 駆動の
 * 動的列テーブル (ClassificationsTable: 列名 = itemFields[].label、code+name 集約、assessment 配色、
 * 行背景) で描画する。schema 未指定 / 列が組めない場合は従来の GenericTable に fallback (回帰ゼロ)。
 */

import {
  axisValueField,
  findMasterRowByCode,
  masterTemplateVarForKey,
  resolveFormatAxes,
  splitCodeValue,
  hiddenPostDetailFieldKeys,
  type AiCategoryMaster,
  type AiOutputFormatSchema,
  type ResolvedPostDetailVisibility,
} from '@citizen-walk/shared-types';
import { ClassificationsTable } from './ClassificationsTable';
import { buildClassificationColumns } from './classificationTableRenderer';
import { resolveChipColor, type AiCategoryMastersResult } from './useAiCategoryMasters';

/** key 名を軽く humanize (snake_case / camelCase → 単語区切り)。schema に label が無いキーの最終 fallback。 */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
}

/**
 * 出力キーに現れる一般語の日本語訳 (モード非依存の **語** 辞書)。
 *
 * M3 の `fields[].label` が未設定 / キーと同一 (= 管理者が表示名を付けていない) のとき、
 * UI に内部キー (`emotion_reason` 等) を出さないための最終 fallback。モード固有の概念ではなく
 * 「reason=理由」「code=コード」といった一般語だけを持ち、全語が引けたときのみ採用する
 * (1 語でも未知なら従来の humanize)。恒久的な表示名は M3 の label で設定する。
 */
const GENERIC_WORD_JA: Record<string, string> = {
  description: '説明',
  summary: '要約',
  detail: '詳細',
  details: '詳細',
  comment: 'コメント',
  comments: 'コメント',
  note: 'メモ',
  notes: 'メモ',
  reason: '理由',
  reasons: '理由',
  basis: '根拠',
  reasoning: '根拠',
  text: 'テキスト',
  title: 'タイトル',
  name: '名称',
  code: 'コード',
  codes: 'コード',
  emotion: '感情',
  emotions: '感情',
  category: '分類',
  categories: '分類',
  classification: '分類',
  classifications: '分類',
  sub: '補助',
  score: 'スコア',
  scores: 'スコア',
  level: 'レベル',
  rank: '順位',
  assessment: '評価',
  insight: '見解',
  professional: '専門家',
  tag: 'タグ',
  tags: 'タグ',
  type: '種別',
  types: '種別',
};

/** キーを一般語辞書で日本語化 (全語が引けたときのみ)。引けなければ null。 */
function japaneseKeyFallback(key: string): string | null {
  const tokens = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_-]+/)
    .filter(Boolean);
  if (tokens.length === 0) return null;
  const words: string[] = [];
  for (const t of tokens) {
    const ja = GENERIC_WORD_JA[t.toLowerCase()];
    if (!ja) return null;
    words.push(ja);
  }
  return words.join('');
}

/**
 * 出力フォーマット schema の fields[] から「キー → 日本語ラベル」マップを構築 (2026-06-13)。
 * UI に内部キー (description 等) を出さず管理者設定の日本語ラベルを使うため (UI no-internal-keys ルール)。
 * object-array の itemFields / enum-array の reasonField もネストして拾う。
 */
function buildLabelMap(schema: AiOutputFormatSchema | null | undefined): Record<string, string> {
  const map: Record<string, string> = {};
  const visit = (fields: readonly unknown[] | undefined): void => {
    for (const raw of fields ?? []) {
      const f = raw as {
        key?: string;
        label?: string;
        reasonField?: { key?: string; label?: string };
        itemFields?: unknown[];
      };
      if (f.key && f.label) map[f.key] = f.label;
      if (f.reasonField?.key && f.reasonField.label) map[f.reasonField.key] = f.reasonField.label;
      if (f.itemFields) visit(f.itemFields);
    }
  };
  visit(schema?.fields as unknown[] | undefined);
  return map;
}

/**
 * 出力キー → 参照する M2 カテゴリマスタ の対応表を作る (モード非依存、issue #33)。
 *
 * M3 の分類軸 (明示 `classificationAxes`、無ければ `fields[]` から導出) の各軸について、
 *   1. 軸の `masterId` が指すマスタ
 *   2. 無ければ キー由来の templateVar (`category_codes` → `categoryMaster`、
 *      `gql_code` / `sub_gql` → `gqlMaster`、`emotions` → `emotionMaster`)
 * の順でマスタを解決する。ここに載ったキーの値は「コード/ラベル」として chip 表示し、
 * マスタで日本語名・配色を解決する (ECW のハードコードだった感情/対象分類 chip の一般化)。
 */
function buildAxisMasterMap(
  schema: AiOutputFormatSchema | null | undefined,
  resolver: AiCategoryMastersResult | null | undefined,
): Map<string, AiCategoryMaster | undefined> {
  const map = new Map<string, AiCategoryMaster | undefined>();
  if (!resolver) return map;
  for (const { axis } of resolveFormatAxes(schema)) {
    const keys = [axisValueField(axis), axis.subField].filter((k): k is string => !!k);
    for (const key of keys) {
      const byId = axis.masterId
        ? (resolver.masters ?? []).find((m) => m.id === axis.masterId)
        : undefined;
      map.set(key, byId ?? resolver.byTemplateVar.get(masterTemplateVarForKey(key)));
    }
  }
  return map;
}

/**
 * 1 コード (or ラベル) の表示名を M2 マスタで解決する。
 * - 値がマスタの `category` そのもの (感情ラベル等) → その日本語名
 * - 値がコード ("A-01" / "GQL-05") → "コード 名称【大分類】"
 * - 未解決 → 値をそのまま (degrade)
 */
function masterChipLabel(code: string, master: AiCategoryMaster | undefined): string {
  const row =
    findMasterRowByCode(master, code) ?? (master?.rows ?? []).find((r) => r.category === code);
  if (!row) return code;
  if (row.category === code) return row.category;
  const major = row.major ? `【${row.major}】` : '';
  return `${code} ${row.category ?? ''}${major}`.trim();
}

/** 分類軸フィールドの値を、マスタ解決した色付き chip 群で表示する。 */
function MasterChips({ codes, master }: { codes: string[]; master: AiCategoryMaster | undefined }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {codes.map((code, idx) => {
        const row =
          findMasterRowByCode(master, code) ??
          (master?.rows ?? []).find((r) => r.category === code);
        const color = row ? resolveChipColor(row, master) : '#94A3B8';
        const label = masterChipLabel(code, master);
        return (
          <span
            key={`${code}-${idx}`}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium text-white"
            style={{ backgroundColor: color }}
            title={label}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

/** 値が「集約スコアっぽいオブジェクト」(sum/max/min/avg を含む数値 Record) かを判定。 */
function isScoreObject(v: unknown): v is Record<string, number> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  if (keys.length === 0) return false;
  const scoreKeys = new Set(['sum', 'max', 'min', 'avg']);
  // 全 key が score 系 かつ 値が数値のとき score object とみなす。
  return keys.every(
    (k) => scoreKeys.has(k) && typeof (v as Record<string, unknown>)[k] === 'number',
  );
}

/** 値が「Record の配列」(classifications 等の object-array) かを判定。 */
function isRecordArray(v: unknown): v is Record<string, unknown>[] {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((el) => typeof el === 'object' && el !== null && !Array.isArray(el))
  );
}

/** 値が「文字列配列」(enum-array 等) かを判定。 */
function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.length > 0 && v.every((el) => typeof el === 'string');
}

/** 任意の cell 値を 1 行テキストに整形 (表の中身用)。 */
function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map((el) => formatCell(el)).join(', ');
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}

/** string[] を chip 群で表示 (mode 別ブロックの chip 慣習に合わせた slate 配色)。 */
function GenericChips({ values }: { values: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((label, idx) => (
        <span
          key={`${label}-${idx}`}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-200 text-slate-700"
        >
          {label}
        </span>
      ))}
    </div>
  );
}

/** {sum,max,avg} 等の集約スコアを inline 表示 (Bousai/Walker の score 表示慣習を踏襲)。 */
function GenericScore({ score }: { score: Record<string, number> }) {
  const order: Array<keyof typeof score> = ['sum', 'max', 'min', 'avg'];
  const present = order.filter((k) => typeof score[k] === 'number');
  return (
    <span className="text-[11px] text-slate-600">
      {present.map((k, i) => (
        <span key={k}>
          {i > 0 && <span className="mx-1 text-slate-300">/</span>}
          <span className="text-slate-400">{k}:</span>{' '}
          <strong>{k === 'avg' ? (score[k] ?? 0).toFixed(1) : score[k]}</strong>
        </span>
      ))}
    </span>
  );
}

/**
 * Record<string,unknown>[] (classifications 等) をコンパクトな表で描画。
 * 列 = 全行に出現するキーの和集合 (各行の最初の出現順を保つ)、行 = 配列要素。
 */
function GenericTable({ rows }: { rows: Record<string, unknown>[] }) {
  // 列順は「各行のキーを出現順に追加」した和集合 (最初に出たキーから順に並ぶ)。
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) {
        seen.add(k);
        columns.push(k);
      }
    }
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[11px] border-collapse">
        <thead>
          <tr className="border-b border-slate-200">
            {columns.map((col) => (
              <th
                key={col}
                scope="col"
                className="text-left font-medium text-slate-500 px-1.5 py-1 whitespace-nowrap"
              >
                {humanizeKey(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b border-slate-100 last:border-b-0">
              {columns.map((col) => (
                <td key={col} className="text-slate-700 px-1.5 py-1 align-top">
                  {formatCell(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** schema の object-array の key (classifications 等)。無ければ null。 */
function objectArrayKey(schema: AiOutputFormatSchema | null | undefined): string | null {
  if (!schema) return null;
  // buildClassificationColumns が列を組めるかも併せて確認する (object-array + itemFields)。
  if (!buildClassificationColumns(schema)) return null;
  const arr = schema.fields?.find((f) => f.kind === 'object-array');
  return arr?.key ?? 'classifications';
}

/** 1 つの generic field (key, value) を型に応じて描画。 */
function GenericField({
  fieldKey,
  value,
  schema,
  visibility,
  labelOf,
  axisMasters,
}: {
  fieldKey: string;
  value: unknown;
  /** 列名動的化レベル 3 (docs/105): classifications field を fields[] 駆動の動的列で描画するため。 */
  schema?: AiOutputFormatSchema | null;
  /** issue #24: 集約スコア/テーブル/分類軸/評価ラベル の表示可否 (解決済、未指定は全表示)。 */
  visibility?: ResolvedPostDetailVisibility;
  /** キー → 表示ラベル (schema の日本語 label、無ければ humanize) を解決する関数。 */
  labelOf: (key: string) => string;
  /** 分類軸キー → 参照マスタ (chip の日本語名/配色解決用)。空なら chip 化しない。 */
  axisMasters?: Map<string, AiCategoryMaster | undefined>;
}) {
  const label = labelOf(fieldKey);

  // 分類軸フィールド (コード列 / ラベル配列) → マスタ解決した色付き chip 群。
  // "C-16,C-15,B-06" のようなカンマ区切り文字列も、単一コード "GQL-05" も同じ扱い。
  if (axisMasters?.has(fieldKey)) {
    // issue #24: 分類軸 (classificationAxes) が off なら非表示。
    if (visibility && !visibility.classificationAxes) return null;
    const codes = splitCodeValue(value);
    if (codes.length > 0) {
      return (
        <div data-generic-key={fieldKey}>
          <div className="text-[11px] text-slate-500 mb-1">{label}</div>
          <MasterChips codes={codes} master={axisMasters.get(fieldKey)} />
        </div>
      );
    }
  }

  // string[] → chip 群
  if (isStringArray(value)) {
    return (
      <div data-generic-key={fieldKey}>
        <div className="text-[11px] text-slate-500 mb-1">{label}</div>
        <GenericChips values={value} />
      </div>
    );
  }

  // Record<string,unknown>[] (classifications 等) → 表
  if (isRecordArray(value)) {
    // 列名動的化レベル 3: schema の object-array key に一致する field は動的列テーブルで描画。
    // (admin が fields[] で列を増減すれば追従。それ以外の record-array は従来の GenericTable。)
    const objKey = objectArrayKey(schema);
    const useDynamicTable = objKey !== null && fieldKey === objKey;
    // issue #24: テーブル状の出力 (fieldsTable) が off なら fields[] 駆動の動的テーブルは出さない。
    if (useDynamicTable && visibility && !visibility.fieldsTable) return null;
    return (
      <div data-generic-key={fieldKey}>
        <div className="text-[11px] text-slate-500 mb-1">{label}</div>
        {useDynamicTable && schema ? (
          // issue #24: 分類軸/評価ラベルの列除外に visibility を渡す。
          <ClassificationsTable schema={schema} classifications={value} visibility={visibility} />
        ) : (
          <GenericTable rows={value} />
        )}
      </div>
    );
  }

  // {sum,max,avg} 等 → 集約スコア inline
  if (isScoreObject(value)) {
    // issue #24: 集約スコア (aggregateScores) が off なら非表示。
    if (visibility && !visibility.aggregateScores) return null;
    return (
      <p className="text-[11px] text-slate-600" data-generic-key={fieldKey}>
        <span className="text-slate-400">{label}:</span> <GenericScore score={value} />
      </p>
    );
  }

  // 文字列
  if (typeof value === 'string') {
    if (value === '') return null;
    return (
      <p className="text-[11px] text-slate-700" data-generic-key={fieldKey}>
        <span className="text-slate-400">{label}:</span> {value}
      </p>
    );
  }

  // 数値 / boolean
  if (typeof value === 'number' || typeof value === 'boolean') {
    return (
      <p className="text-[11px] text-slate-700" data-generic-key={fieldKey}>
        <span className="text-slate-400">{label}:</span> <strong>{String(value)}</strong>
      </p>
    );
  }

  // null / undefined / 空配列 は非表示
  if (value === null || value === undefined) return null;
  if (Array.isArray(value) && value.length === 0) return null;

  // その他 (ネスト object 等) → JSON 文字列に fallback
  return (
    <p className="text-[11px] text-slate-700 break-all" data-generic-key={fieldKey}>
      <span className="text-slate-400">{label}:</span> {formatCell(value)}
    </p>
  );
}

export interface GenericAiResultBlockProps {
  /** `PostMinimal.aiAnalysisGeneric` (非空オブジェクト前提、空 / null は呼び出し側で描画しない)。 */
  generic: Record<string, unknown>;
  /**
   * 列名動的化レベル 3 (docs/105 / old2 移植): `AiOutputFormat.schema`。
   * classifications field を fields[] 駆動の動的列で描画するために使う。未指定 (null) の場合は
   * record-array を従来の humanize-key GenericTable で描画する (回帰ゼロ)。
   */
  schema?: AiOutputFormatSchema | null;
  /** issue #24: AI 出力フォーマット要素の表示可否 (解決済)。未指定は全表示 (後方互換)。 */
  visibility?: ResolvedPostDetailVisibility;
  /**
   * 主表示モード (custom mode 等、mode 別 ResultBlock が無い場合)。
   * true: 折りたたまず開いた状態で、通常の見出し付きセクションとして描画する。
   * false (既定): 従来どおり `<details>` 折りたたみの debug セクション (mode 別結果と併存時)。
   */
  primary?: boolean;
  /** primary 時のセクション見出し。既定 "🤖 AI 解析結果"。 */
  title?: string;
  /** 囲みの枠・背景を付けない (フォルダ型のタブの内容としてタブの枠に入れるとき、docs/29)。 */
  embedded?: boolean;
  /**
   * M2 カテゴリマスタ解決結果 (issue #33)。渡すと分類軸フィールド (コード列 / ラベル配列) を
   * マスタ解決した色付き chip で表示する (コード → 日本語名、マスタ配色)。
   * 未指定なら従来どおりの素の表示 (回帰ゼロ)。
   */
  resolver?: AiCategoryMastersResult | null;
  /** 解析日時 / 設定 ID の footer (mode 別ブロックと同じ体裁)。 */
  footer?: React.ReactNode;
}

/**
 * 汎用 (列名動的化レベル 3) AI 解析結果セクション。
 * - primary=false (既定): 折りたたみ (`<details>`、初期は閉) の debug 表示 (mode 別結果と併存時)。
 * - primary=true: mode 別 ResultBlock が無い custom mode 等の主表示。開いた通常セクションで描画する。
 */
export function GenericAiResultBlock({
  generic,
  schema,
  visibility,
  primary = false,
  title = '🤖 AI 解析結果',
  embedded = false,
  resolver,
  footer,
}: GenericAiResultBlockProps) {
  const entries = Object.entries(generic);
  if (entries.length === 0) return null;
  // 表示順は schema.fields[] の並び順を正とする (admin が M3 出力フォーマットの並びで制御できる)。
  // Firestore は map のキー順を保持しないため Object.entries の順には依存しない。
  // schema 未指定 / fields に無いキー (集約スコア等) は元の順で末尾に出す (回帰ゼロ)。
  const orderedKeys: string[] = [];
  const seen = new Set<string>();
  for (const f of schema?.fields ?? []) {
    if (f.key in generic && !seen.has(f.key)) {
      orderedKeys.push(f.key);
      seen.add(f.key);
    }
    const rf = f.kind === 'enum-array' || f.kind === 'code-list-string' ? f.reasonField : undefined;
    if (rf && rf.key in generic && !seen.has(rf.key)) {
      orderedKeys.push(rf.key);
      seen.add(rf.key);
    }
  }
  for (const [k] of entries) {
    if (!seen.has(k)) {
      orderedKeys.push(k);
      seen.add(k);
    }
  }
  // docs/29 §5-1: 出力フォーマットで「投稿詳細に表示しない」にした項目は出さない (データは残っている)。
  const hiddenKeys = hiddenPostDetailFieldKeys(schema);
  const shownKeys = orderedKeys.filter((k) => !hiddenKeys.has(k));
  if (shownKeys.length === 0) return null;
  // UI には内部キー (description 等) ではなく schema の日本語 label を出す (no-internal-keys ルール)。
  const labelMap = buildLabelMap(schema);
  // 表示名は M3 の label を最優先。label 未設定 / キーそのままの場合は一般語辞書 → humanize。
  const labelOf = (key: string): string => {
    const configured = labelMap[key];
    if (configured && configured !== key) return configured;
    return japaneseKeyFallback(key) ?? humanizeKey(key);
  };
  // 分類軸キー → マスタ (chip の日本語名 / 配色解決用)。resolver 未指定なら空 = 従来表示。
  const axisMasters = buildAxisMasterMap(schema, resolver);
  const body = (
    <div className="space-y-2 mt-2">
      {shownKeys.map((key) => (
        <GenericField
          key={key}
          fieldKey={key}
          value={generic[key]}
          schema={schema}
          visibility={visibility}
          labelOf={labelOf}
          axisMasters={axisMasters}
        />
      ))}
    </div>
  );
  if (primary) {
    return (
      <section
        className={embedded ? '' : 'mb-3 p-3 rounded-md bg-sky-50/50 border border-sky-200'}
        data-testid="generic-ai-result"
      >
        <h3 className="text-xs font-semibold text-sky-900 mb-2">{title}</h3>
        {body}
        {footer}
      </section>
    );
  }
  return (
    <section
      className="mb-3 p-3 rounded-md bg-slate-50/70 border border-slate-200"
      data-testid="generic-ai-result"
    >
      <details>
        <summary className="cursor-pointer text-xs font-semibold text-slate-700 hover:text-slate-900">
          🤖 AI 解析結果
        </summary>
        {body}
      </details>
    </section>
  );
}
